import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { FieldErrors, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { kpiConfigByIdMapAtom } from '../../../../../services/state/KPIConfigState';
import { captureFormValidationError } from '../../../../../util/SentryUtils';
import { formToTemplateDataModel } from '../custom-kpi-template-mappers';
import { CustomKpiTemplateFormValues } from '../../../../../schemas/kpi-template.schema';
import { useCreateConsolidatedTemplate, useUpdateConsolidatedTemplate } from '../state/custom-template-state';

export function useSaveCustomKpiTemplate() {
  const navigate = useNavigate();
  const { handleSubmit, watch, reset } = useFormContext<CustomKpiTemplateFormValues>();
  const kpiConfigMap = useAtomValue(kpiConfigByIdMapAtom);

  const { mutateAsync: create, isPending: isCreating } = useCreateConsolidatedTemplate();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateConsolidatedTemplate();

  const id = watch('id');
  const isUpdate = typeof id === 'number' && id > 0;

  const onSubmit = useCallback(
    async (formData: CustomKpiTemplateFormValues) => {
      const payload = formToTemplateDataModel(formData, kpiConfigMap);
      if (isUpdate) {
        await update(payload);
        reset(formData);
      } else {
        const created = await create(payload);
        navigate(`/${ROUTES.KPI}/${ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION}/${created.id}`, {
          state: { isProgrammatic: true },
        });
      }
    },
    [isUpdate, create, update, reset, navigate, kpiConfigMap]
  );

  const onValidationError = useCallback((errors: FieldErrors<CustomKpiTemplateFormValues>) => {
    console.error('CustomKpiTemplateForm validation errors:', errors);
    captureFormValidationError('CustomKpiTemplateForm', errors);
  }, []);

  const save = useCallback(() => {
    return handleSubmit(onSubmit, onValidationError)();
  }, [handleSubmit, onSubmit, onValidationError]);

  return { save, isUpdate, isPending: isCreating || isUpdating };
}
